import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient, apiDelete, apiGet, apiPatch, apiPost } from '@/lib/api';

export type RecordType = 'lead' | 'contact' | 'company' | 'deal';

export const RECORD_LABELS: Record<RecordType, string> = {
  lead: 'Leads',
  contact: 'Contacts',
  company: 'Companies',
  deal: 'Deals',
};

/** Where a record opens in the app. */
export function recordPath(type: string | null | undefined, id: string | null | undefined): string | null {
  if (!type || !id) return null;
  if (type === 'deal') return `/crm/deals/${id}`;
  if (type === 'lead') return `/crm/leads/${id}`;
  if (type === 'contact') return `/crm/contacts/${id}`;
  if (type === 'company') return `/crm/companies/${id}`;
  return null;
}

/** Saves a CSV the API streamed back. */
export async function downloadCsv(url: string, fallbackName: string) {
  const response = await apiClient.get(url, { responseType: 'blob' });
  const disposition = String(response.headers['content-disposition'] ?? '');
  const name = disposition.match(/filename="([^"]+)"/)?.[1] ?? fallbackName;
  const href = URL.createObjectURL(response.data as Blob);
  const link = document.createElement('a');
  link.href = href;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(href), 1_000);
}

// ── Imports ──────────────────────────────────────────────────────────────────

export type ImportField = {
  key: string;
  label: string;
  kind: 'text' | 'email' | 'phone' | 'money' | 'number' | 'date' | 'bool' | 'enum';
  required?: boolean;
  options?: string[];
  aliases?: string[];
};

export type ImportJob = {
  id: string;
  entityType: RecordType;
  fileName: string;
  status: 'RUNNING' | 'COMPLETED' | 'UNDONE';
  duplicateMode: 'skip' | 'update' | 'create';
  totalRows: number;
  createdCount: number;
  updatedCount: number;
  skippedCount: number;
  failedCount: number;
  createdAt: string;
  finishedAt: string | null;
  undoneAt: string | null;
  createdBy?: { id: string; name: string } | null;
  errors?: { row: number; message: string }[];
};

export function useImportFields(entityType: RecordType) {
  return useQuery({
    queryKey: ['imports', 'fields', entityType],
    queryFn: () => apiGet<ImportField[]>(`/imports/fields/${entityType}`),
  });
}

export function useImports() {
  return useQuery({ queryKey: ['imports', 'list'], queryFn: () => apiGet<ImportJob[]>('/imports') });
}

export function useImport(id: string | null) {
  return useQuery({
    queryKey: ['imports', 'one', id],
    queryFn: () => apiGet<ImportJob>(`/imports/${id}`),
    enabled: Boolean(id),
  });
}

export function useUndoImport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiPost<ImportJob & { removed: number }>(`/imports/${id}/undo`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['imports'] });
      for (const key of ['leads', 'contacts', 'companies', 'deals']) void queryClient.invalidateQueries({ queryKey: [key] });
    },
  });
}

// ── Email ────────────────────────────────────────────────────────────────────

export type TrackedEmail = {
  id: string;
  entityType: string | null;
  entityId: string | null;
  to: string;
  toName: string | null;
  subject: string;
  body: string;
  status: 'SENT' | 'FAILED';
  error: string | null;
  openCount: number;
  firstOpenedAt: string | null;
  lastOpenedAt: string | null;
  clickCount: number;
  firstClickedAt: string | null;
  repliedAt: string | null;
  sentAt: string;
  enrollmentId: string | null;
  stepIndex: number | null;
  sentBy?: { id: string; name: string } | null;
};

export type Mailbox = { id: string; name: string; address: string | null };

export type SalesTemplate = {
  id: string;
  kind: 'TEMPLATE' | 'SNIPPET';
  name: string;
  shortcut: string | null;
  folder: string | null;
  subject: string | null;
  body: string;
  isShared: boolean;
  ownerId: string | null;
  owner: { id: string; name: string } | null;
  usageCount: number;
  canEdit: boolean;
  sent: number;
  opened: number;
  updatedAt: string;
};

export function useTrackedEmails(params: { entityType?: string; entityId?: string; mine?: boolean; page?: number }) {
  return useQuery({
    queryKey: ['sales', 'emails', params],
    queryFn: () =>
      apiGet<{ items: TrackedEmail[]; total: number; page: number; limit: number }>('/sales/emails', {
        params: {
          ...(params.entityType ? { entityType: params.entityType, entityId: params.entityId } : {}),
          ...(params.mine ? { mine: 'true' } : {}),
          page: params.page ?? 1,
        },
      }),
    refetchInterval: 30_000,
  });
}

export function useEmailStats(days = 30) {
  return useQuery({
    queryKey: ['sales', 'emails', 'stats', days],
    queryFn: () =>
      apiGet<{ days: number; sent: number; opened: number; clicked: number; replied: number; openRate: number; clickRate: number; replyRate: number }>(
        '/sales/emails/stats',
        { params: { days } },
      ),
  });
}

export function useMailboxes(enabled = true) {
  return useQuery({ queryKey: ['sales', 'mailboxes'], queryFn: () => apiGet<Mailbox[]>('/sales/emails/mailboxes'), enabled });
}

export function useMergeFields() {
  return useQuery({
    queryKey: ['sales', 'merge-fields'],
    queryFn: () => apiGet<{ key: string; label: string }[]>('/sales/emails/merge-fields'),
    staleTime: Infinity,
  });
}

export function useSendEmail() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      entityType?: string;
      entityId?: string;
      to?: string;
      subject: string;
      body: string;
      templateId?: string;
      channelId?: string;
      track?: boolean;
    }) => apiPost<TrackedEmail>('/sales/emails', body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['sales', 'emails'] });
      void queryClient.invalidateQueries({ queryKey: ['timeline'] });
    },
  });
}

export function useTemplates(kind?: 'TEMPLATE' | 'SNIPPET') {
  return useQuery({
    queryKey: ['sales', 'templates', kind ?? 'all'],
    queryFn: () => apiGet<SalesTemplate[]>('/sales/templates', { params: kind ? { kind } : undefined }),
  });
}

export function useSaveTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: Partial<SalesTemplate> & { id?: string }) =>
      id ? apiPatch<SalesTemplate>(`/sales/templates/${id}`, body) : apiPost<SalesTemplate>('/sales/templates', body),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['sales', 'templates'] }),
  });
}

export function useDeleteTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`/sales/templates/${id}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['sales', 'templates'] }),
  });
}

// ── Sequences ────────────────────────────────────────────────────────────────

export type SequenceStep =
  | { id: string; type: 'email'; delayDays: number; subject: string; body: string; templateId?: string | null }
  | { id: string; type: 'task' | 'call'; delayDays: number; title: string; note?: string | null };

export type SequenceSettings = {
  mailboxChannelId?: string | null;
  sendFrom: number;
  sendTo: number;
  weekdaysOnly: boolean;
  timeZone: string;
  stopOnMeeting: boolean;
  track: boolean;
};

export type Sequence = {
  id: string;
  name: string;
  description: string | null;
  steps: SequenceStep[];
  settings: SequenceSettings | null;
  isActive: boolean;
  createdById: string | null;
  createdAt: string;
  updatedAt: string;
  stepCount: number;
  enrolled: number;
  active: number;
  byStatus: Record<string, number>;
  sent: number;
  opened: number;
  replied: number;
  replyRate: number;
  openRate: number;
};

export type Enrollment = {
  id: string;
  sequenceId: string;
  entityType: 'lead' | 'contact';
  entityId: string;
  email: string;
  name: string | null;
  status: 'ACTIVE' | 'PAUSED' | 'COMPLETED' | 'REPLIED' | 'MEETING_BOOKED' | 'STOPPED' | 'FAILED';
  currentStep: number;
  nextRunAt: string | null;
  stoppedReason: string | null;
  createdAt: string;
  finishedAt: string | null;
  sequence: { id: string; name: string };
  stepCount: number;
  sender: { id: string; name: string } | null;
  emailsSent: number;
  opened: boolean;
  clicked: boolean;
};

export const ENROLLMENT_STATUS: Record<Enrollment['status'], { label: string; className: string }> = {
  ACTIVE: { label: 'Active', className: 'bg-blue-100 text-blue-700' },
  PAUSED: { label: 'Paused', className: 'bg-amber-100 text-amber-700' },
  COMPLETED: { label: 'Finished', className: 'bg-neutral-200 text-neutral-700' },
  REPLIED: { label: 'Replied', className: 'bg-emerald-100 text-emerald-700' },
  MEETING_BOOKED: { label: 'Meeting booked', className: 'bg-emerald-100 text-emerald-700' },
  STOPPED: { label: 'Stopped', className: 'bg-neutral-200 text-neutral-600' },
  FAILED: { label: 'Failed', className: 'bg-red-100 text-red-700' },
};

export function useSequences() {
  return useQuery({ queryKey: ['sales', 'sequences'], queryFn: () => apiGet<Sequence[]>('/sales/sequences') });
}

export function useSequence(id: string | null) {
  return useQuery({
    queryKey: ['sales', 'sequences', id],
    queryFn: () => apiGet<Sequence & { stepStats: { sent: number; opened: number }[] }>(`/sales/sequences/${id}`),
    enabled: Boolean(id),
  });
}

export function useEnrollments(params: { sequenceId?: string | null; entityType?: string; entityId?: string; status?: string }) {
  const { sequenceId, ...query } = params;
  return useQuery({
    queryKey: ['sales', 'enrollments', params],
    queryFn: () =>
      apiGet<Enrollment[]>(sequenceId ? `/sales/sequences/${sequenceId}/enrollments` : '/sales/sequences/enrollments', {
        params: Object.fromEntries(Object.entries(query).filter(([, value]) => value)),
      }),
  });
}

export function useSaveSequence() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id?: string; name: string; description?: string | null; steps: SequenceStep[]; settings: SequenceSettings; isActive: boolean }) =>
      id ? apiPatch<Sequence>(`/sales/sequences/${id}`, body) : apiPost<Sequence>('/sales/sequences', body),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['sales', 'sequences'] }),
  });
}

export function useDeleteSequence() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`/sales/sequences/${id}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['sales', 'sequences'] }),
  });
}

export function useEnroll() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sequenceId, ...body }: { sequenceId: string; targets: { entityType: 'lead' | 'contact'; entityId: string }[]; startAt?: string }) =>
      apiPost<{ enrolled: { entityId: string }[]; skipped: { entityId: string; reason: string }[] }>(`/sales/sequences/${sequenceId}/enroll`, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['sales', 'sequences'] });
      void queryClient.invalidateQueries({ queryKey: ['sales', 'enrollments'] });
      void queryClient.invalidateQueries({ queryKey: ['timeline'] });
    },
  });
}

export function useEnrollmentAction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'pause' | 'resume' | 'stop' }) =>
      apiPost(`/sales/sequences/enrollments/${id}`, { action }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['sales', 'enrollments'] });
      void queryClient.invalidateQueries({ queryKey: ['sales', 'sequences'] });
    },
  });
}

// ── Meetings ─────────────────────────────────────────────────────────────────

export type Weekday = 'sun' | 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat';
export type Availability = Partial<Record<Weekday, [string, string][]>>;

export type MeetingLink = {
  id: string;
  slug: string;
  url: string;
  name: string;
  description: string | null;
  kind: 'PERSONAL' | 'ROUND_ROBIN';
  ownerId: string;
  owner: { id: string; name: string } | null;
  memberIds: string[];
  members: { id: string; name: string }[];
  durationMinutes: number;
  locationType: 'video' | 'phone' | 'in_person' | 'custom';
  location: string | null;
  timezone: string;
  availability: Availability;
  bufferMinutes: number;
  minNoticeHours: number;
  maxDaysAhead: number;
  questions: { id: string; label: string; required: boolean }[] | null;
  createLead: boolean;
  isActive: boolean;
  bookings: number;
  canEdit: boolean;
};

export type Meeting = {
  id: string;
  linkId: string | null;
  link: { id: string; name: string } | null;
  hostId: string;
  host: { id: string; name: string } | null;
  title: string;
  startsAt: string;
  endsAt: string;
  timezone: string;
  location: string | null;
  guestName: string | null;
  guestEmail: string | null;
  guestPhone: string | null;
  answers: Record<string, string> | null;
  entityType: string | null;
  entityId: string | null;
  record: { type: string; id: string; name: string } | null;
  status: 'SCHEDULED' | 'CANCELLED' | 'COMPLETED' | 'NO_SHOW';
  outcome: string | null;
  notes: string | null;
};

export function useMeetingLinks() {
  return useQuery({ queryKey: ['sales', 'meeting-links'], queryFn: () => apiGet<MeetingLink[]>('/sales/meetings/links') });
}

export function useSaveMeetingLink() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: Record<string, unknown> & { id?: string }) =>
      id ? apiPatch<MeetingLink>(`/sales/meetings/links/${id}`, body) : apiPost<MeetingLink>('/sales/meetings/links', body),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['sales', 'meeting-links'] }),
  });
}

export function useDeleteMeetingLink() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`/sales/meetings/links/${id}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['sales', 'meeting-links'] }),
  });
}

export function useMeetings(params: { from?: string; to?: string; entityType?: string; entityId?: string; hostId?: string; status?: string }) {
  return useQuery({
    queryKey: ['sales', 'meetings', params],
    queryFn: () =>
      apiGet<Meeting[]>('/sales/meetings', { params: Object.fromEntries(Object.entries(params).filter(([, value]) => value)) }),
  });
}

export function useCreateMeeting() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => apiPost<Meeting>('/sales/meetings', body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['sales', 'meetings'] });
      void queryClient.invalidateQueries({ queryKey: ['timeline'] });
    },
  });
}

export function useUpdateMeeting() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; status?: Meeting['status']; outcome?: string | null; notes?: string | null }) =>
      apiPatch<Meeting>(`/sales/meetings/${id}`, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['sales', 'meetings'] });
      void queryClient.invalidateQueries({ queryKey: ['timeline'] });
    },
  });
}

// ── Calls ────────────────────────────────────────────────────────────────────

export const CALL_OUTCOMES = [
  ['CONNECTED', 'Connected'],
  ['NO_ANSWER', 'No answer'],
  ['BUSY', 'Busy'],
  ['VOICEMAIL', 'Left voicemail'],
  ['WRONG_NUMBER', 'Wrong number'],
  ['CALLBACK', 'Asked to call back'],
] as const;
export type CallOutcome = (typeof CALL_OUTCOMES)[number][0];

export type Call = {
  id: string;
  userId: string;
  user: { id: string; name: string } | null;
  entityType: string | null;
  entityId: string | null;
  phone: string | null;
  direction: 'OUTBOUND' | 'INBOUND';
  outcome: CallOutcome;
  outcomeLabel: string;
  durationSeconds: number;
  notes: string | null;
  recordingUrl: string | null;
  startedAt: string;
};

export function useCalls(params: { entityType?: string; entityId?: string; userId?: string; outcome?: string; from?: string; to?: string }) {
  return useQuery({
    queryKey: ['sales', 'calls', params],
    queryFn: () => apiGet<Call[]>('/sales/calls', { params: Object.fromEntries(Object.entries(params).filter(([, value]) => value)) }),
  });
}

export function useLogCall() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => apiPost<Call>('/sales/calls', body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['sales', 'calls'] });
      void queryClient.invalidateQueries({ queryKey: ['timeline'] });
      void queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
}

export function useDeleteCall() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`/sales/calls/${id}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['sales', 'calls'] }),
  });
}

export const formatDuration = (seconds: number) => {
  if (!seconds) return '—';
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return minutes ? `${minutes}m ${rest ? `${rest}s` : ''}`.trim() : `${rest}s`;
};

// ── Playbooks ────────────────────────────────────────────────────────────────

export type PlaybookQuestion = {
  id: string;
  type: 'text' | 'textarea' | 'select' | 'multiselect' | 'checkbox' | 'number';
  label: string;
  help?: string | null;
  options?: string[];
  required: boolean;
};

export type Playbook = {
  id: string;
  name: string;
  description: string | null;
  entityTypes: RecordType[];
  questions: PlaybookQuestion[];
  isActive: boolean;
  runCount: number;
  updatedAt: string;
};

export function usePlaybooks(entityType?: string) {
  return useQuery({
    queryKey: ['sales', 'playbooks', entityType ?? 'all'],
    queryFn: () => apiGet<Playbook[]>('/sales/playbooks', { params: entityType ? { entityType } : undefined }),
  });
}

export function useSavePlaybook() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: Partial<Playbook> & { id?: string }) =>
      id ? apiPatch<Playbook>(`/sales/playbooks/${id}`, body) : apiPost<Playbook>('/sales/playbooks', body),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['sales', 'playbooks'] }),
  });
}

export function useDeletePlaybook() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`/sales/playbooks/${id}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['sales', 'playbooks'] }),
  });
}

export function useRunPlaybook() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; entityType: string; entityId: string; answers: Record<string, unknown> }) =>
      apiPost(`/sales/playbooks/${id}/run`, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['sales', 'playbooks'] });
      void queryClient.invalidateQueries({ queryKey: ['timeline'] });
    },
  });
}

// ── Reports and targets ──────────────────────────────────────────────────────

export const QUOTA_METRICS = [
  ['revenue', 'Revenue won'],
  ['deals_won', 'Deals won'],
  ['calls', 'Calls'],
  ['meetings', 'Meetings held'],
  ['emails', 'Emails sent'],
] as const;

export type ActivityRow = {
  user: { id: string; name: string };
  calls: number;
  callsConnected: number;
  talkSeconds: number;
  emails: number;
  emailsOpened: number;
  meetingsHeld: number;
  meetingsBooked: number;
  tasksCompleted: number;
  notes: number;
  leadsCreated: number;
  dealsWon: number;
  revenue: string;
  goals: { metric: string; target: string; actual: string; progress: number }[];
};

export function useActivityReport(period: string) {
  return useQuery({
    queryKey: ['sales', 'activity', period],
    queryFn: () =>
      apiGet<{ from: string; to: string; period: string; currency: string; canSetQuotas: boolean; rows: ActivityRow[] }>(
        '/sales/activity-report',
        { params: { period } },
      ),
  });
}

export function useQuotas(period: string, enabled: boolean) {
  return useQuery({
    queryKey: ['sales', 'quotas', period],
    queryFn: () =>
      apiGet<{ period: string; users: { id: string; name: string }[]; quotas: { userId: string; metric: string; target: string }[] }>(
        `/sales/quotas/${period}`,
      ),
    enabled,
  });
}

export function useSaveQuotas() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { period: string; items: { userId: string; metric: string; target: string }[] }) =>
      apiClient.put('/sales/quotas', body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['sales', 'quotas'] });
      void queryClient.invalidateQueries({ queryKey: ['sales', 'activity'] });
    },
  });
}

// ── Recycle bin ──────────────────────────────────────────────────────────────

export function useRecycleBin(type: RecordType, enabled: boolean) {
  return useQuery({
    queryKey: ['sales', 'recycle', type],
    queryFn: () =>
      apiGet<{ retentionDays: number; rows: { id: string; name: string; detail: string | null; deletedAt: string }[] }>(
        '/sales/recycle-bin',
        { params: { type } },
      ),
    enabled,
  });
}

export function useRestore() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ type, id }: { type: RecordType; id: string }) => apiPost(`/sales/recycle-bin/${type}/${id}/restore`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['sales', 'recycle'] });
      for (const key of ['leads', 'contacts', 'companies', 'deals']) void queryClient.invalidateQueries({ queryKey: [key] });
    },
  });
}
